import {IssueTypeToken} from '@github-ui/issue-type-token'
import {useAnalytics} from '@github-ui/use-analytics'
import {InfoIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton} from '@primer/react'
import {useCallback, useState} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueTypeAddedEvent$key} from './__generated__/IssueTypeAddedEvent.graphql'
import type {IssueTypeAddedEventRationales$data} from './__generated__/IssueTypeAddedEventRationales.graphql'
import styles from './IssueTypeEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type IssueTypeAddedEventProps = {
  queryRef: IssueTypeAddedEvent$key & {createdAt?: string}
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLDivElement | null>
  repositoryNameWithOwner?: string
  rationale?: IssueTypeAddedEventRationales$data['eventRationales'][number]
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueTypeAddedEventRationalesFragment = graphql`
  fragment IssueTypeAddedEventRationales on Issue {
    # eslint-disable-next-line relay/unused-fields
    eventRationales {
      actor {
        login
      }
      rationale
      issueEvent {
        ... on IssueTypeAddedEvent {
          id
        }
        ... on IssueTypeRemovedEvent {
          id
        }
        ... on IssueTypeChangedEvent {
          id
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypeAddedEventFragment = graphql`
  fragment IssueTypeAddedEvent on IssueTypeAddedEvent {
    databaseId
    actor {
      ...TimelineRowEventActor
    }
    createdAt
    issueType {
      name
      color
    }
  }
`

export function IssueTypeAddedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryNameWithOwner,
  rationale,
}: IssueTypeAddedEventProps) {
  const {actor, createdAt, issueType, databaseId} = useFragment(IssueTypeAddedEventFragment, queryRef)
  const [showRationale, setShowRationale] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics()
  const toggleRationale = useCallback(() => {
    setShowRationale(prev => {
      const next = !prev
      if (next) {
        sendAnalyticsEvent('issue_viewer.timeline.rationale_click', 'ISSUE_TYPE_ADDED', {
          issueEventDatabaseId: String(databaseId),
          issueTypeName: issueType?.name,
          rationaleLength: rationale?.rationale?.length,
        })
      }
      return next
    })
  }, [sendAnalyticsEvent, databaseId, issueType?.name, rationale?.rationale])
  const closeRationale = useCallback(() => setShowRationale(false), [])

  if (!issueType) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const getTooltipText = (isTextTruncated: boolean) => {
    return isTextTruncated ? issueType.name : undefined
  }

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueOpenedIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.issueTypeAdded.leading}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={issueType.name}
            color={issueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${issueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {rationale && (
          <AnchoredOverlay
            open={showRationale}
            onOpen={toggleRationale}
            onClose={closeRationale}
            renderAnchor={props => (
              // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
              <IconButton
                {...props}
                icon={InfoIcon}
                variant="invisible"
                size="small"
                aria-label="Rationale"
                unsafeDisableTooltip
                className={styles.rationaleIcon}
              />
            )}
          >
            <div className={styles.rationaleOverlay}>{rationale.rationale}</div>
          </AnchoredOverlay>
        )}
        {`${LABELS.timeline.issueTypeAdded.trailing} `}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
